/**
 * Calendar Store — local-first calendar derived from reminderStore.
 *
 * Reminder events are derived from the shared reminderStore (local data).
 * Google events are fetched from server only on pull-to-refresh / reconnect.
 * This keeps calendar and reminders tab always consistent.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { CalendarEvent } from '../types/api';
import * as api from '../api/client';

/** Minimum ms between automatic re-fetches (5 minutes) */
const STALE_THRESHOLD = 5 * 60 * 1000;

interface CalendarState {
  googleEvents: CalendarEvent[];
  isLoading: boolean;
  selectedDate: string;
  lastFetchedAt: number; // epoch ms of last successful fetch
  fetchError: boolean;

  // Actions
  setSelectedDate: (date: string) => void;
  loadGoogleEvents: (force?: boolean) => Promise<void>;
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set, get) => ({
      googleEvents: [],
      isLoading: false,
      selectedDate: toLocalDateStr(new Date()),
      lastFetchedAt: 0,
      fetchError: false,

      setSelectedDate: (date) => set({ selectedDate: date }),

      loadGoogleEvents: async (force = false) => {
        const { isLoading, lastFetchedAt } = get();
        if (isLoading) return;
        // Skip if cache is fresh (unless forced by pull-to-refresh)
        if (!force && Date.now() - lastFetchedAt < STALE_THRESHOLD) return;

        set({ isLoading: true });
        try {
          const data = await api.getCalendarEvents({});
          const gEvents = (data.events || []).filter(
            (e: CalendarEvent) => e.type === 'google'
          );
          set({ googleEvents: gEvents, isLoading: false, lastFetchedAt: Date.now(), fetchError: false });
        } catch (err) {
          console.warn('[Calendar] loadGoogleEvents failed:', err);
          set({ isLoading: false, fetchError: true });
        }
      },
    }),
    {
      name: 'fm-calendar',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        googleEvents: state.googleEvents,
        selectedDate: state.selectedDate,
        lastFetchedAt: state.lastFetchedAt,
      }),
    },
  ),
);
